import { Alert, Box, Button, Group } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import useFetch from '../../../helpers/useFetch';
import ErrorMessage from '../../ErrorMessage';
import Spinner from '../../Spinner';
import GoogleAdForm from './GoogleAdForm';

const GoogleAdNewForm = ({ from, onCreate, onBack }) => {
  const fetch = useFetch();
  const [googleAd, setGoogleAd] = useState();
  const [userError, setUserError] = useState();
  const [loadError, setLoadError] = useState();
  // undefined = still checking; null = check skipped/failed (fail open);
  // object = { enabledConversionActions, recentConversions } (recentConversions null = unmeasured)
  const [conversionReadiness, setConversionReadiness] = useState(undefined);

  const { accountId, domainId } = useParams();
  const customerId = useSelector((state) => state.google.account?.adAccountId);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (from) {
          const { googleAd } = await fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/googleAds/${from}`);
          googleAd.name = `Copy of ${googleAd.name}`;
          delete googleAd.id;
          setGoogleAd(googleAd);
        } else {
          setGoogleAd({
            name: 'New google campaign',
            biddingStrategyType: 'MAXIMIZE_CONVERSIONS',
            budget: null,
            targetCpa: null,
            targetRoas: null,
            isRetail: false,
            durationInDays: 7,
            finalUrl: null,
            finalMobileUrl: null,
            urlExpansionOptOut: false,
            languages: [],
            locations: [],
            accountDefaultConversionGoals: [],
          });
        }
      } catch (error) {
        setLoadError(error);
      }
    };

    fetchData();
  }, [from, fetch, accountId, domainId]);

  // Performance Max bids on conversions, so it won't serve without active conversion tracking.
  // Disable the form when the account has no enabled conversion actions; warn (non-blocking) when
  // actions exist but record 0 conversions. Fail open on error so a transient Google API issue
  // can't lock users out of creating ads.
  const checkConversionReadiness = useCallback(() => {
    if (!customerId) {
      setConversionReadiness(null);
      return;
    }
    setConversionReadiness(undefined);
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/google/${customerId}/conversionReadiness`)
      .then(({ conversionReadiness }) => setConversionReadiness(conversionReadiness))
      .catch(() => setConversionReadiness(null));
  }, [fetch, accountId, domainId, customerId]);

  useEffect(() => {
    checkConversionReadiness();
  }, [checkConversionReadiness]);

  const create = (googleAd) => {
    return fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/googleAds`, {
      method: 'POST',
      body: JSON.stringify(googleAd),
    })
      .then(({ googleAd }) => onCreate(googleAd))
      .catch((error) => {
        setUserError(error);
        throw error;
      });
  };

  if (loadError) {
    return <ErrorMessage message={loadError.message} />;
  }

  if (googleAd === undefined || conversionReadiness === undefined) {
    return <Spinner />;
  }

  const noConversionTracking = conversionReadiness && conversionReadiness.enabledConversionActions === 0;
  const zeroConversions =
    conversionReadiness && conversionReadiness.enabledConversionActions > 0 && conversionReadiness.recentConversions === 0;

  // Hard block: account has no conversion tracking at all. Show the form (read-only) so the user
  // sees what they're configuring, but keep it non-interactive so the ad can't be created.
  if (noConversionTracking) {
    return (
      <>
        <Alert variant="light" color="yellow" icon={<IconAlertCircle size={16} />} title="Conversion tracking required" mb="md">
          <Box mb="sm">
            Your Google Ads account has <strong>no conversion tracking set up</strong>. Performance Max campaigns use
            conversion-based bidding (Maximize conversions / conversion value), so Google will not bid and the campaign{' '}
            <strong>won&apos;t serve any impressions</strong> until at least one conversion action is set up and
            recording conversions in your Google Ads account.
          </Box>
          <Box mb="md">
            Please set up conversion tracking in Google Ads first (e.g. a Purchase conversion). The form below is
            disabled until then. Once you&apos;ve set it up, use <strong>Re-check</strong> to refresh without leaving
            this page.
          </Box>
          <Group gap="sm">
            <Button variant="default" onClick={onBack}>
              Back
            </Button>
            <Button variant="light" color="yellow" onClick={checkConversionReadiness}>
              Re-check
            </Button>
          </Group>
        </Alert>
        <Box inert style={{ opacity: 0.55, pointerEvents: 'none' }}>
          <GoogleAdForm googleAd={googleAd} saveGoogleAd={create} onBack={onBack} />
        </Box>
      </>
    );
  }

  return (
    <>
      {zeroConversions ? (
        <Alert variant="light" color="yellow" icon={<IconAlertCircle size={16} />} mb="md">
          Your Google Ads account has conversion tracking, but it has recorded <strong>0 conversions in the last 30
          days</strong>. Performance Max bids on conversions, so it may not serve until conversions start coming in — if
          this is a new or test conversion, verify real conversion tracking is live before relying on this campaign.
        </Alert>
      ) : null}

      <GoogleAdForm googleAd={googleAd} saveGoogleAd={create} onBack={onBack} />

      {userError ? (
        <Box ta="right">
          <ErrorMessage message={userError.message} />
        </Box>
      ) : null}
    </>
  );
};

export default GoogleAdNewForm;
